export interface ScoreState {
  cleanliness: number;
  lastCorrect: boolean | null;
  answeredCount: number;
  correctCount: number;
  streak: number;
  lastComboBonus: number;
}

export type ScoreListener = (state: ScoreState) => void;

const MIN_SCORE = 0;
const MAX_SCORE = 100;
const START_SCORE = 50;
const WRONG_PENALTY = 5;
/** Streaks below this don't earn a bonus; above it, +1 per extra streak step, capped at +5. */
const COMBO_THRESHOLD = 3;
const COMBO_MAX_BONUS = 5;

export class ScoreController {
  private state: ScoreState = {
    cleanliness: START_SCORE,
    lastCorrect: null,
    answeredCount: 0,
    correctCount: 0,
    streak: 0,
    lastComboBonus: 0,
  };

  private readonly listeners = new Set<ScoreListener>();

  getState(): ScoreState {
    return { ...this.state };
  }

  onChange(listener: ScoreListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** @param correctDelta score awarded when `correct` is true; a flat penalty applies when false. */
  applyAnswer(correct: boolean, correctDelta: number): ScoreState {
    const streak = correct ? this.state.streak + 1 : 0;
    const comboBonus = correct && streak >= COMBO_THRESHOLD ? Math.min(streak - (COMBO_THRESHOLD - 1), COMBO_MAX_BONUS) : 0;
    const delta = correct ? correctDelta + comboBonus : -WRONG_PENALTY;
    const cleanliness = clamp(this.state.cleanliness + delta, MIN_SCORE, MAX_SCORE);

    this.state = {
      cleanliness,
      lastCorrect: correct,
      answeredCount: this.state.answeredCount + 1,
      correctCount: this.state.correctCount + (correct ? 1 : 0),
      streak,
      lastComboBonus: comboBonus,
    };

    this.emit();
    return this.getState();
  }

  reset(): void {
    this.state = {
      cleanliness: START_SCORE,
      lastCorrect: null,
      answeredCount: 0,
      correctCount: 0,
      streak: 0,
      lastComboBonus: 0,
    };
    this.emit();
  }

  private emit(): void {
    const snapshot = this.getState();
    for (const listener of this.listeners) listener(snapshot);
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
